<script lang="ts" setup>
import { ChartOverlay } from '@forgd/ui-kit/charts/chart-overlay'
import { ChartTitle } from '@forgd/ui-kit/charts/chart-title'
import { chartColors } from '@forgd/ui-kit/charts/lib/colors'
import type { LoanUtilizationPoint } from '#public-research/utils/mm-monitoring.data'
import { formatValue } from '@forgd/formatter'
import { LoadingAnimation } from '@forgd/ui-kit/blocks/animations'
import { ColorKey, ColorKeyLabel, ColorKeyValue } from '@forgd/ui-kit/color-key'
import { VisAxis, VisLine, VisXYContainer } from '@unovis/vue'

const props = defineProps<{
  data: LoanUtilizationPoint[]
  startLabel: string
  endLabel: string
  loading?: boolean
}>()

const height = 136 + 22
const depthColor = chartColors.tokenDesigner[0]
const volumeColor = chartColors.tokenDesigner[1]

const legend: { label: string, color: string, key: 'depthPct' | 'volumePct' }[] = [
  { label: 'Depth Loan Utilization (%)', color: depthColor, key: 'depthPct' },
  { label: 'Volume Loan Utilization (%)', color: volumeColor, key: 'volumePct' },
]

const lastIndex = computed(() => Math.max(props.data.length - 1, 0))
const xTicks = computed(() => [0, lastIndex.value])

function xTickFormat(v: number) {
  if (v === 0) {
    return props.startLabel
  }
  if (v === lastIndex.value) {
    return props.endLabel
  }
  return ''
}

function pctFormat(v: number | null | undefined) {
  return v == null ? 'N/A' : formatValue(v, 'percent:2')
}

const x = (d: LoanUtilizationPoint) => d.index
// unovis treats null as a finite 0 (drawing missing days at the baseline); return
// undefined so days with no utilization data render as gaps instead.
const yDepth = (d: LoanUtilizationPoint) => d.depthPct ?? undefined
const yVolume = (d: LoanUtilizationPoint) => d.volumePct ?? undefined
const margin = { left: 52, right: 56, top: 8, bottom: 24 }

// Live utilization can far exceed the demo-era 6%/12% ceilings, so fit each axis
// to its data (keeping the old values as a floor) rather than clipping the lines.
function domainMax(values: (number | null)[], floor: number): number {
  const max = Math.max(0, ...values.filter((v): v is number => v != null))
  return max <= floor ? floor : Math.ceil((max * 1.1) / 5) * 5
}
const depthDomain = computed<[number, number]>(() => [0, domainMax(props.data.map(d => d.depthPct), 6)])
const volumeDomain = computed<[number, number]>(() => [0, domainMax(props.data.map(d => d.volumePct), 12)])
</script>

<template>
  <div data-el="PrMmLoanUtilizationChart" class="flex size-full flex-col">
    <ChartTitle text="Loan Utilization (%) | Date" />
    <div v-if="loading" class="flex w-full items-center justify-center" :style="{ height: `${height}px` }">
      <LoadingAnimation />
    </div>
    <PrChartPlaceholder v-else :height="height">
      <div class="dual-axis relative w-full">
        <VisXYContainer
          :data="data"
          :height="height"
          :y-domain="depthDomain"
          :margin="margin"
          :auto-margin="false"
          class="layer"
        >
          <VisAxis
            type="x"
            :x="x"
            :tick-values="xTicks"
            :tick-format="xTickFormat"
            :tick-line="false"
            :grid-line="false"
          />
          <VisAxis
            type="y"
            position="left"
            :tick-text-color="depthColor"
            :tick-format="pctFormat"
            :num-ticks="7"
            :tick-line="false"
            :grid-line="true"
          />
          <VisLine :x="x" :y="yDepth" :color="depthColor" />
        </VisXYContainer>

        <VisXYContainer
          :data="data"
          :height="height"
          :y-domain="volumeDomain"
          :margin="margin"
          :auto-margin="false"
          class="layer"
        >
          <VisAxis
            type="y"
            position="right"
            :tick-text-color="volumeColor"
            :tick-format="pctFormat"
            :num-ticks="7"
            :tick-line="false"
            :grid-line="false"
          />
          <VisLine :x="x" :y="yVolume" :color="volumeColor" />
          <ChartOverlay>
            <template #default="{ d }">
              <div class="flex min-w-55 flex-col gap-1">
                <ColorKey v-for="item in legend" :key="item.label" :color="item.color" layout="justify">
                  <ColorKeyLabel>{{ item.label }}</ColorKeyLabel>
                  <ColorKeyValue>{{ pctFormat(d[item.key]) }}</ColorKeyValue>
                </ColorKey>
              </div>
            </template>
          </ChartOverlay>
        </VisXYContainer>
      </div>
    </PrChartPlaceholder>
    <div class="mt-4 flex shrink-0 flex-wrap gap-x-5 gap-y-1">
      <ColorKey v-for="item in legend" :key="item.label" :color="item.color" layout="inline" class="shrink-0">
        <ColorKeyLabel>{{ item.label }}</ColorKeyLabel>
      </ColorKey>
    </div>
  </div>
</template>

<style scoped>
.dual-axis {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
}
.dual-axis .layer {
  grid-column-start: 1;
  grid-row-start: 1;
}
</style>
